import { FamilyFestComingWithEnum, FamilyFestTravelModeEnum } from '@application/enums/family-fest-registration-enums';
import { convertLocalToBackend } from '@application/utilities/date-helper';
const normalized = (value) => String(value ?? '')
    .toLowerCase()
    .trim();
const optionalString = (value) => {
    const text = String(value ?? '').trim();
    return text.length > 0 ? text : null;
};
const optionalDateString = (value) => {
    if (!value)
        return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : convertLocalToBackend(value);
    }
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (!trimmedValue)
            return null;
        const parsedDate = new Date(trimmedValue);
        return Number.isNaN(parsedDate.getTime()) ? trimmedValue : convertLocalToBackend(parsedDate);
    }
    return null;
};
const nullableBoolean = (value) => {
    if (typeof value === 'boolean')
        return value;
    const text = normalized(value);
    if (text === 'true')
        return true;
    if (text === 'false')
        return false;
    return null;
};
const booleanValue = (value, fallback = false) => {
    const parsed = nullableBoolean(value);
    return parsed ?? fallback;
};
const optionalNumber = (value) => {
    if (value === null || value === undefined || value === '')
        return null;
    const numeric = Number(value);
    if (!Number.isFinite(numeric))
        return null;
    return numeric > 0 ? Math.trunc(numeric) : null;
};
const optionalDecimal = (value) => {
    if (value === null || value === undefined || value === '')
        return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};
const mapComingWith = (value) => {
    switch (normalized(value)) {
        case 'family':
            return FamilyFestComingWithEnum.Family;
        case 'guest':
        case 'introduction':
            return FamilyFestComingWithEnum.Guest;
        case 'alone':
        default:
            return FamilyFestComingWithEnum.Alone;
    }
};
const mapTravelMode = (value) => {
    switch (normalized(value)) {
        case 'auto':
            return FamilyFestTravelModeEnum.Auto;
        case 'bus':
            return FamilyFestTravelModeEnum.Bus;
        case 'plane':
            return FamilyFestTravelModeEnum.Plane;
        default:
            return null;
    }
};
const mapPrimary = (person, groupVehicleCount) => ({
    id: person?.id,
    firstName: optionalString(person?.firstName),
    middleName: optionalString(person?.middleName),
    lastName: optionalString(person?.lastName),
    email: optionalString(person?.email),
    companyId: optionalString(person?.company),
    addressLine: optionalString(person?.Address),
    addressCountry: optionalString(person?.addressCountry),
    addressPlaceId: optionalString(person?.addressPlaceId),
    addressLatitude: optionalDecimal(person?.addressLatitude),
    addressLongitude: optionalDecimal(person?.addressLongitude),
    postalCode: optionalString(person?.postalcode),
    houseNumber: optionalString(person?.houseNumber),
    houseNumberAddition: optionalString(person?.addition),
    phone: optionalString(person?.Phone),
    phoneCountryCode: optionalString(person?.PhoneCountryCode),
    dateOfBirth: optionalDateString(person?.DateOfBirth),
    isComingToEvent: booleanValue(person?.eventChoice, true),
    attendanceStatus: optionalNumber(person?.attendanceStatus),
    reasonForCancellation: optionalString(person?.reasonForCancellation),
    travelMode: mapTravelMode(person?.TravelMode),
    pickupLocationId: normalized(person?.TravelMode) === 'plane' || normalized(person?.TravelMode) === 'auto' ? null : optionalString(person?.transportLocation),
    preferredAirport: normalized(person?.TravelMode) === 'plane' ? optionalString(person?.transportLocation) : null,
    wantsVdkAccommodation: nullableBoolean(person?.accommodationRequired),
    isWheelchairDependent: booleanValue(person?.Option, false),
    mobilityComments: optionalString(person?.supportComments),
    hasHotelBooking: nullableBoolean(person?.bookingInfoRequired),
    hotelBookingComments: optionalString(person?.bookingInfo),
    vehicleCount: groupVehicleCount,
});
const mapMember = (person) => ({
    id: person?.id,
    firstName: String(person?.firstName ?? '').trim(),
    middleName: optionalString(person?.middleName),
    lastName: String(person?.lastName ?? '').trim(),
    relationshipToPrimary: optionalString(person?.relationshipToYou),
    email: optionalString(person?.email),
    dateOfBirth: optionalDateString(person?.DateOfBirth),
    phone: optionalString(person?.Phone),
    phoneCountryCode: optionalString(person?.PhoneCountryCode),
    isComingToEvent: booleanValue(person?.eventChoice, true),
    attendanceStatus: optionalNumber(person?.attendanceStatus),
    reasonForCancellation: optionalString(person?.reasonForCancellation),
    addressLine: optionalString(person?.Address),
    addressCountry: optionalString(person?.addressCountry),
    addressPlaceId: optionalString(person?.addressPlaceId),
    addressLatitude: optionalDecimal(person?.addressLatitude),
    addressLongitude: optionalDecimal(person?.addressLongitude),
    postalCode: optionalString(person?.postalcode),
    houseNumber: optionalString(person?.houseNumber),
    houseNumberAddition: optionalString(person?.addition),
    travelMode: mapTravelMode(person?.TravelMode),
    pickupLocationId: normalized(person?.TravelMode) === 'plane' || normalized(person?.TravelMode) === 'auto' ? null : optionalString(person?.transportLocation),
    preferredAirport: normalized(person?.TravelMode) === 'plane' ? optionalString(person?.transportLocation) : null,
    wantsVdkAccommodation: nullableBoolean(person?.accommodationRequired),
    isWheelchairDependent: booleanValue(person?.Option, false),
    mobilityComments: optionalString(person?.supportComments),
    hasHotelBooking: nullableBoolean(person?.bookingInfoRequired),
    hotelBookingComments: optionalString(person?.bookingInfo)
});
/** Body for POST .../attendees/register-admin (Happiness portal). */
export const buildFamilyFestAdminRegistrationBody = (values, deletedMemberIds = []) => {
    const persons = Array.isArray(values?.persons) ? values.persons : [];
    const [primaryPerson = {}, ...memberPersons] = persons;
    const comingWith = normalized(primaryPerson?.ComingWith);
    const travelMode = normalized(primaryPerson?.TravelMode);
    let groupVehicleCount = null;
    if (comingWith === 'alone' && travelMode === 'auto') {
        groupVehicleCount = 1;
    }
    else {
        groupVehicleCount = optionalNumber(values?.vehicleCount);
    }
    // Alone has no members: the form only hides the member cards, so drop them here and delete the saved ones
    // (same result as the public form, which marks saved members for deletion when ComingWith changes).
    if (comingWith === 'alone') {
        const savedMemberIds = memberPersons
            .map((member) => String(member?.id ?? '').trim())
            .filter(Boolean);
        return {
            comingWith: mapComingWith(primaryPerson?.ComingWith),
            primary: mapPrimary(primaryPerson, groupVehicleCount),
            members: [],
            memberstobedeletedIds: [...new Set([...deletedMemberIds, ...savedMemberIds])]
        };
    }
    return {
        comingWith: mapComingWith(primaryPerson?.ComingWith),
        primary: mapPrimary(primaryPerson, groupVehicleCount),
        members: memberPersons
            .filter((member) => !deletedMemberIds?.includes(member?.id))
            .map((member, index) => mapMember(member)),
        memberstobedeletedIds: deletedMemberIds
    };
};
